/**
 * The dashboard provider stack.
 *
 * Split in two on purpose:
 *  - {@link AppProviders} — always-on, session-independent layers: theme, the
 *    TanStack Query client, the session manager, the API client, wallet/web3,
 *    and the toast surface. Mounted once at the root.
 *  - {@link SessionScopedProviders} — the layers that need a LIVE session
 *    (feature flags, tenant context + branding, RBAC permissions). Mounted
 *    inside the authenticated shell, where `useSession()` is guaranteed.
 */
import * as React from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import type { ApiClient } from "@authe/fe-commons/api";
import type { SessionManager } from "@authe/fe-commons/session";
import { FeatureFlags, type QaConfig } from "@authe/fe-commons/flags";
import { ThemeProvider } from "@authe/ui-core/theme";
import { SessionProvider, useSession } from "@authe/ui-core/session";

import { ImpersonationBanner } from "./ImpersonationBanner";
import { impersonationHandoffFailure } from "./session";
import { ApiProvider } from "@authe/ui-core/api";
import { FlagsProvider } from "@authe/ui-core/flags";
import { TenantProvider } from "@authe/ui-core/tenant";
import { PermissionProvider } from "@authe/ui-core/permissions";
import { Toaster } from "@authe/ui-core/providers";
import { TooltipProvider, notifySuccess, setColumnPrefsIdentity } from "@authe/ui-core";
import { ObservabilityProvider } from "@authe/ui-core/observability";
import { Web3Provider, WalletProvider } from "@authe/web3";
import { StreamingProvider, type StreamingClient } from "@authe/streaming";
import type { GraphQLClient } from "@authe/fe-commons/graphql";
import type { AutheToken } from "@authe/fe-commons/api";
import { GraphqlProvider, preconnectGraphqlEndpoint } from "./graphql";
import {
  THEME_STORAGE_KEY,
  DEFAULT_BRAND,
  transportFlagDefaults,
  uiFlagDefaults,
  FLAG_OVERRIDE_STORAGE_KEY,
} from "@/config";
import { appEnv } from "@/config/env";
import { wagmiConfig } from "./web3";
import { setRoutingToken } from "./routing-token";
import { NameOverridesProvider, mergeTenancyNames, useNameOverrides } from "./tenancy-names";
import { DashboardConfidenceCutoffs } from "./DashboardConfidenceCutoffs";
import { DashboardPrefsProvider } from "./dashboardPrefs";
import { TransportPolicyProvider } from "./transportPolicy";
import { DashboardPlan } from "./DashboardPlan";
import { ActiveTeamProvider } from "./activeTeam";
import { DashboardScopeHints } from "./DashboardScopeHints";
import { DashboardGraphDefaults } from "./graphDefaults";
import { DashboardSavedViews } from "./DashboardSavedViews";
import { DashboardExportLedger } from "./DashboardExportLedger";
import { setDisplayPrefsIdentity } from "./prefs";
import { useStorageIdentity } from "./storageIdentity";

export interface AppProvidersProps {
  manager: SessionManager;
  apiClient: ApiClient;
  queryClient: QueryClient;
  /** Live-stream client; null when no stream endpoint is configured (Live mode off). */
  streamingClient?: StreamingClient | null;
  /** HTTP GraphQL read client; null when `PRIVATE_API_GRAPHQL_URL` is unset (REST only). */
  graphqlClient?: GraphQLClient | null;
  children: React.ReactNode;
}

/**
 * Mount `<StreamingProvider>` only when a client exists AND a live session is
 * established. Gating on auth is essential: `<StreamingProvider>` calls
 * `client.start()` on mount, which pulls a bearer (`getTokenSilently`) and opens
 * the socket. If that runs during the Auth0 redirect callback (this provider
 * sits above `<RequireSession>`), the streaming token fetch races the in-flight
 * `handleRedirectCallback` — the concurrent silent-auth/lock contention makes the
 * session's own `loadSession` token fetch fail ("loadSession failed despite
 * isAuthenticated"), leaving the app stuck on "Signing in", and the keepalive
 * ping fires unauthenticated (401). Waiting for `isAuthenticated` means the token
 * is cached and valid before the socket ever starts. Streaming hooks degrade to
 * a no-op (via `useOptionalStreamingClient`) until then.
 */
function MaybeStreaming({
  client,
  children,
}: {
  client: StreamingClient | null | undefined;
  children: React.ReactNode;
}): React.ReactElement {
  const { isAuthenticated } = useSession();
  if (!client || !isAuthenticated) return <>{children}</>;
  return <StreamingProvider client={client}>{children}</StreamingProvider>;
}

export function AppProviders({
  manager,
  apiClient,
  queryClient,
  streamingClient,
  graphqlClient,
  children,
}: AppProvidersProps): React.ReactElement {
  return (
    <ThemeProvider storageKey={THEME_STORAGE_KEY} defaultMode="system" defaultBrand={DEFAULT_BRAND}>
      <ObservabilityProvider env={appEnv} forwardLogs>
      <TooltipProvider delayDuration={200}>
        <QueryClientProvider client={queryClient}>
        <SessionProvider manager={manager}>
          <ApiProvider client={apiClient}>
           <GraphqlProvider client={graphqlClient ?? null}>
            {/*
              Wallet/web3 layer. `Web3Provider` supplies the wagmi (EVM) context
              and shares the app QueryClient; `WalletProvider` is the chain-
              agnostic hub (EVM + Solana + Bitcoin injected adapters) that powers
              `WalletMenu`. Both sit app-wide so the header chip and the Wallet
              page see the same connection state.
            */}
            <Web3Provider config={wagmiConfig} queryClient={queryClient}>
              {/*
                autoConnect is OFF on purpose. Its default (true) eagerly
                hydrates the injected EVM + Solana + Bitcoin adapters on mount,
                which makes multi-chain injected wallets (e.g. Phantom) pop their
                connect prompt on every page refresh. Wallets now connect only on
                explicit user action (the header chip / Wallet page). wagmi's own
                silent `reconnectOnMount` still restores an already-authorized EVM
                session without a prompt.
              */}
              <WalletProvider autoConnect={false}>
                <MaybeStreaming client={streamingClient}>
                  {children}
                  <Toaster />
                </MaybeStreaming>
              </WalletProvider>
            </Web3Provider>
           </GraphqlProvider>
          </ApiProvider>
        </SessionProvider>
        </QueryClientProvider>
      </TooltipProvider>
      </ObservabilityProvider>
    </ThemeProvider>
  );
}

/**
 * One-time "you're signed in" toast.
 *
 * The user's name + workspace live in the JWT, but nothing surfaces them after a
 * login — and a tenant switch mints a NEW token (a fresh login event) behind a
 * full reload, so the user can lose track of which workspace they landed in.
 * This pops a friendly confirmation once per (user, tenant) per browser tab:
 * `sessionStorage` survives the tenant-switch reload, so switching tenants keys
 * a new entry and re-announces, while ordinary navigation/token-refresh stays
 * quiet. High-severity live events still toast via the notification centre.
 */
function SessionWelcomeToast(): null {
  const { session } = useSession();

  React.useEffect(() => {
    if (!session) return;
    const userId = session.user.id ?? "anon";
    const tenantId = session.tenant.tenantId ?? "none";
    const key = `dashboard.welcome.${userId}.${tenantId}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // Private mode / storage disabled — skip the dedupe, but avoid a loop by
      // only firing on this mount.
    }

    const name = session.names.userName ?? session.user.name ?? session.user.email ?? "there";
    // Names only — never the raw ids.
    //
    // This used to fall back to `session.tenant.orgId` / `tenantId`, which is
    // how a reader ended up being told they had landed in
    // `org_41403f9e2d1a4ed69c474b19f72779f7 · ten_5ffee9934e534ac3be0f1368d3570309`.
    // That is not a worse version of the answer, it is the absence of one: the
    // whole reason this toast exists is that a tenant switch mints a new token
    // behind a full reload and the user can lose track of WHICH workspace they
    // landed in, and an opaque id settles that question for nobody. A `null`
    // name means "not resolved" (see `TenancyNames`), so when neither resolves
    // the toast simply confirms the sign-in and says nothing about the scope —
    // which is what we actually know. The switcher in the top bar carries the
    // same names and stays correct once they arrive.
    const scope = [session.names.orgName, session.names.tenantName]
      .filter((p): p is string => Boolean(p))
      .join(" · ");
    notifySuccess(`Signed in as ${name}`, {
      description: scope ? `Workspace ${scope}` : undefined,
    });
  }, [session]);

  return null;
}

/**
 * Flags + tenant + permissions sourced from the live session's claims. Render
 * only where a session exists (inside `<RequireSession>`); falls back to safe
 * empties otherwise so it never throws while the guard redirects.
 *
 * Wraps the session-scoped layers in `<NameOverridesProvider>` so a rename
 * handler anywhere below can optimistically echo a new friendly name (which
 * `<SessionScopedLayers>` overlays onto the token-resolved names before they
 * reach `<TenantProvider>`).
 */
/**
 * Points the display-preference keyspaces at whoever is signed in.
 *
 * Renders nothing: it is the one place the session identity reaches the module
 * singletons every surface imports (`app/prefs`, and `@authe/ui-core`'s table
 * column preferences). Without it those preferences are written app-wide — one
 * keyspace shared by every user and tenant on the browser, which on a shared
 * workstation hands the next analyst the previous one's view. Re-points on a
 * tenant or user switch, and back to the app-only identity on sign-out.
 *
 * Both keyspaces are pointed HERE rather than each store finding the session
 * for itself: they must never disagree about whose preferences are on screen,
 * and one component that runs above every page is the only way to guarantee it.
 */
export function DisplayPrefsIdentity(): null {
  const identity = useStorageIdentity();
  // In render, not an effect: every surface below reads preferences during its
  // own first render, and an effect would let that first paint come from the
  // previous identity's keyspace.
  setDisplayPrefsIdentity(identity);
  // Which columns a table shows, in what order and how wide. Same defect, one
  // layer down: the keys were bare, so the second analyst on a shared machine
  // inherited the first one's layout and it survived a tenant switch.
  setColumnPrefsIdentity(identity);
  return null;
}

export function SessionScopedProviders({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <NameOverridesProvider>
      <SessionScopedLayers>{children}</SessionScopedLayers>
    </NameOverridesProvider>
  );
}

function SessionScopedLayers({ children }: { children: React.ReactNode }): React.ReactElement {
  const { session } = useSession();
  const nameOverrides = useNameOverrides();

  // Feed the ApiClient's region ROUTER the decoded token. Done in render (not
  // an effect) on purpose: this component is an ancestor of every page, so its
  // render runs before any child's fetch effect — guaranteeing the token is
  // present the first time a request resolves its region/host. The token merges
  // the namespaced id-token claims (carrying `region_config` / `continent`) with
  // the raw access token.
  //
  // `session` is read once at bootstrap and never re-polled, so `__raw` here is
  // a page-load snapshot that goes stale. That is fine for the routing claims —
  // a region assignment doesn't change under you — and deliberately NOT what
  // authenticates the request: `ApiClient` takes its bearer from the live
  // `getAccessToken()`. See `routing-token.ts`.
  React.useMemo(() => {
    if (!session) {
      setRoutingToken(null);
      return;
    }
    const idClaims = session.idClaims as Record<string, unknown>;
    setRoutingToken({
      ...idClaims,
      __raw: session.accessToken ?? "",
      sub: typeof idClaims.sub === "string" ? idClaims.sub : "",
    } satisfies AutheToken);
    // Token snapshot is now live, so the GraphQL endpoint resolves to the
    // caller's REGIONAL AppSync custom domain. Warm DNS+TLS to it now (before
    // any page's first read effect fires) so the first composed read is fast.
    preconnectGraphqlEndpoint();
  }, [session]);

  // Transport defaults (which API each operation uses) come from the central
  // `config/transport` switchboard; Settings › APIs writes per-user overrides
  // into the SAME override keyspace, and precedence is `override > claim >
  // default` — so a tenant claim or a user override still wins.
  const flags = React.useMemo(
    () =>
      new FeatureFlags({
        defaults: { ...transportFlagDefaults(), ...uiFlagDefaults() },
        claims: session?.claims.feature_flags ?? {},
        qaConfig: (session?.claims.qa_config as QaConfig | undefined) ?? null,
        overrideStorageKey: FLAG_OVERRIDE_STORAGE_KEY,
      }),
    [session],
  );

  // Overlay any optimistic rename echoes onto the token-resolved names so a
  // just-saved customer/org/tenant name shows immediately, before the token
  // round-trip catches up.
  const names = React.useMemo(
    () => (session ? mergeTenancyNames(session.names, session.tenant, nameOverrides) : null),
    [session, nameOverrides],
  );

  // No session yet → deny-all permissions + a global/empty tenant. The route
  // guard is already redirecting, so this content won't actually paint.
  if (!session) {
    return (
      <FlagsProvider flags={flags}>
        <TenantProvider tenant={{ orgId: null, tenantId: null }}>
          <PermissionProvider rolePermissions={{}} isLoading>
            {/* A handoff that was presented and refused leaves no session, so
                this is the only place the operator can be told why the thing
                they clicked did nothing. */}
            <ImpersonationBanner claims={null} failure={impersonationHandoffFailure()} />
            <DashboardPrefsProvider>
              {/* Carries the stored REST/GraphQL layers onto the flag store and
                  publishes the plan ceiling. Inside the prefs provider (it reads
                  it), inside FlagsProvider (it writes it). */}
              {/* The caller's plan, published once for every gate in the app — the
                  transport ceiling below, the nav entries, the settings rail. Above
                  TransportPolicyProvider, which reads its ceiling from this context
                  rather than resolving a plan of its own. */}
              <DashboardPlan>
              <TransportPolicyProvider>
              <DashboardSavedViews>
                <DashboardExportLedger>
                <DashboardScopeHints>
                  {/* The scope's confidence cutoffs, for every badge that grades a
                    score. Under TenantProvider so a tenant switch re-resolves
                    them; degrades to platform defaults while it loads or if the
                    read fails. */}
                <DashboardConfidenceCutoffs>
                  <DashboardGraphDefaults>{children}</DashboardGraphDefaults>
                </DashboardConfidenceCutoffs>
                </DashboardScopeHints>
                </DashboardExportLedger>
              </DashboardSavedViews>
              </TransportPolicyProvider>
              </DashboardPlan>
            </DashboardPrefsProvider>
          </PermissionProvider>
        </TenantProvider>
      </FlagsProvider>
    );
  }

  return (
    <FlagsProvider flags={flags}>
      <TenantProvider tenant={session.tenant} names={names ?? session.names}>
        <PermissionProvider rolePermissions={session.claims.role_permissions ?? {}}>
          {/* Above everything, including the toast: an impersonated session is
              indistinguishable from an ordinary one without it, and that is the
              danger. Renders null for a normal session, so mounting it is free. */}
          <ImpersonationBanner
            claims={session.claims as never}
            targetSub={session.user.id}
          />
          <SessionWelcomeToast />
          {/* Which team the app is working in. Above the collaborative
              surfaces because the assignee picker and the mention roster both
              scope to it; under TenantProvider so a tenant switch re-keys the
              stored selection. */}
          <ActiveTeamProvider>
          <DashboardPrefsProvider>
            {/* Which API each read uses: the stored plan / workspace / personal
                layers onto the flag store, plus the plan ceiling. Inside the
                prefs provider because it reads those layers, inside
                FlagsProvider because it writes them — and above everything that
                fetches, so the first read already resolves on the right one. */}
            {/* The caller's plan, published once for every gate in the app — the
                transport ceiling below, the nav entries, the settings rail. Above
                TransportPolicyProvider, which reads its ceiling from this context
                rather than resolving a plan of its own. */}
            <DashboardPlan>
            <TransportPolicyProvider>
            {/* Saved-views source + identity for every table that offers them.
                Under TenantProvider so a tenant switch re-keys the stored views. */}
            <DashboardSavedViews>
              {/* Every export under here writes a ledger row (who exported
                  what, from which view). Beside the saved-views seam because
                  the two answer one question together: an export is
                  reproducible only if the view it came from is recorded. */}
              <DashboardExportLedger>
              {/* The tenancy pills' context: this app's policy table, its saved
                  defaults and its settings route. Under the prefs provider it
                  adapts. */}
              <DashboardScopeHints>
                {/* Graph display chrome + media/brand seams, declared once for
                    every graph surface. Sits under TenantProvider so the brand
                    accents resolve against the tenant's own overrides. */}
                <DashboardGraphDefaults>{children}</DashboardGraphDefaults>
              </DashboardScopeHints>
              </DashboardExportLedger>
            </DashboardSavedViews>
            </TransportPolicyProvider>
            </DashboardPlan>
          </DashboardPrefsProvider>
          </ActiveTeamProvider>
        </PermissionProvider>
      </TenantProvider>
    </FlagsProvider>
  );
}
